import { FileSourceEnum } from '@systemeio/file-manager'
import { FormInput } from '@systemeio/ui'
import { DigitalResourcesTypeEnum } from 'modules/digital-product/enums/digital-resources-type-enum'
import { ResourceFileType } from 'modules/digital-product/hooks/use-resource-files'
import {
  DigitalProductChangeInterface,
  DigitalProductErrorInterface,
} from 'modules/digital-product/types/digital-product-interface'
import ItemSteps from 'modules/stores/store/components/item-steps'
import React, { Dispatch, FC, SetStateAction } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { ProductDownloadItemErrorsType } from '../../../types/product-interface'
import { ConfirmationEmail } from './confirmation-email'
import { FileUploaderWithListedFiles } from 'shared/components/file-uploader/file-uploader-with-listed-files'

type Props = {
  tempProduct: DigitalProductChangeInterface
  setTempProduct: Dispatch<SetStateAction<DigitalProductChangeInterface>>
  tempFiles: ResourceFileType[]
  setTempFiles: Dispatch<SetStateAction<ResourceFileType[]>>
  isResourceFilesLoading?: boolean
  productItemErrors: Pick<ProductDownloadItemErrorsType, 'digitalProduct.resources'>
  digitalProductErrors: DigitalProductErrorInterface
}

export const DigitalDownload: FC<Props> = ({
  tempProduct,
  setTempProduct,
  tempFiles,
  setTempFiles,
  isResourceFilesLoading,
  productItemErrors,
  digitalProductErrors,
}) => {
  const { t } = useLocoTranslation()
  return (
    <ItemSteps
      steps={[
        {
          key: 'digital download',
          title: t('dashboard.digital_product.select_name'),
          element: (
            <FormInput
              label={t('dashboard.funnel.configuration.offer.digital_product_name')}
              value={tempProduct.name}
              onChange={e => setTempProduct(prev => ({ ...prev, name: e.target.value }))}
              required
              error={digitalProductErrors.name}
            />
          ),
        },
        {
          key: 'resources',
          title: t('dashboard.digital_product.resources.digital_download.upload_resources'),
          element: (
            <FileUploaderWithListedFiles
              source={FileSourceEnum.DigitalProductResource}
              files={tempFiles}
              isPreFetching={isResourceFilesLoading}
              onSelect={file => {
                setTempFiles(prev => [...prev, file])
                setTempProduct(prev => ({
                  ...prev,
                  resources: [
                    ...prev.resources,
                    {
                      type: DigitalResourcesTypeEnum.downloadable_file,
                      dataFile: file.id,
                    },
                  ],
                }))
              }}
              onDelete={file => {
                setTempFiles(prev => prev.filter(item => item.id !== file.id))
                setTempProduct(prev => ({
                  ...prev,
                  resources: prev.resources.filter(item => {
                    if (item.type === DigitalResourcesTypeEnum.downloadable_file)
                      return item.dataFile !== file.id
                    return true
                  }),
                }))
              }}
              error={
                digitalProductErrors.resources.length === 1 &&
                typeof digitalProductErrors.resources[0] === 'string'
                  ? digitalProductErrors.resources[0]
                  : productItemErrors['digitalProduct.resources']
              }
              errors={digitalProductErrors.resources.map(error => {
                if (typeof error === 'object') {
                  return Object.values(error).join(' ')
                }
              })}
            />
          ),
        },
        {
          key: 'confirmation email',
          title: t('dashboard.store.product.download.confirmation_email.title'),
          element: (
            <ConfirmationEmail
              confirmationEmail={tempProduct.confirmationEmail}
              onChange={confirmationEmail =>
                setTempProduct(prev => ({ ...prev, confirmationEmail }))
              }
              errors={digitalProductErrors.confirmationEmail}
            />
          ),
        },
      ]}
    />
  )
}
